import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '../../../Components/TanstackTable'
import useReportTable from '../useReportTable'
import { PropertyData, PropertyReportProps } from './types'
import { getPropertyOveralls } from './utils'

const PropertyReportTable = (props: PropertyReportProps) => {
    const { reportsData, isLoading } = props
    const { t } = useTranslation()

    const reports = useMemo(() => reportsData?.data || [], [reportsData])
    const months = useMemo(() => reportsData?.timePeriod || [], [reportsData])

    const columns = useMemo<ColumnDef<PropertyData>[]>(() => {
        const dataSourceColumn: ColumnDef<PropertyData> = {
            id: 'dataSource',
            header: t('app.common.dataSource'),
            enableSorting: false,
            accessorFn: (row) => row.dataSource?.name,
        }

        const monthColumns: ColumnDef<PropertyData>[] = months?.map(({ month, monthName, year }) => ({
            id: `${month}/${year}`,
            header: monthName,
            enableSorting: false,
            cell: (info) => {
                const propertyTotal = info.row.original.realizedPropertyTotals?.find((pt) => pt?.month === month)
                if (!propertyTotal) return null
                return (
                    <div className="d-flex gap-2 justify-content-center">
                        {propertyTotal.totalSale != null && <span>{propertyTotal.totalSale}</span>}
                        {propertyTotal.totalRent != null && <span className="text-danger">{propertyTotal.totalRent}</span>}
                    </div>
                )
            },
        }))

        const totalColumn: ColumnDef<PropertyData> = {
            id: 'dataSourceTotal',
            header: t('app.common.total'),
            enableSorting: false,
            cell: (info) => <div className="text-center fw-bolder">{info.row.original.realizedPropertyGrandTotal}</div>,
        }

        return [dataSourceColumn, ...monthColumns, totalColumn]
    }, [months, t])

    const tableFooter = useMemo(() => {
        const { monthOveralls, grandOverall } = getPropertyOveralls(reports, months)

        return (
            <tr>
                <td>{t('app.common.overall')}</td>
                {monthOveralls.map((overall, i) => (
                    <td key={i} className="text-center">
                        {overall.count}
                    </td>
                ))}
                <td className="text-center fw-bolder">{grandOverall}</td>
            </tr>
        )
    }, [reports, months, t])

    const table = useReportTable(columns, reports)

    return (
        <DataTable
            table={table}
            tableFooter={tableFooter}
            isLoading={isLoading}
            theadClass="dmn-table-header"
        />
    )
}

export default PropertyReportTable
